import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useNavigate } from 'react-router-dom';
import { PDFDocument, PDFName, PDFString } from 'pdf-lib';
import './css/PdfEditor.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfEditor = () => {
  const navigate = useNavigate();
  const [editorMode, setEditorMode] = useState(null);

  // Estados Modo Individual
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [file, setFile] = useState(null);
  const [links, setLinks] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [tempUrl, setTempUrl] = useState('');

  // Estados Modo Batch
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchLinksPerPage, setBatchLinksPerPage] = useState({});
  const [batchNumPages, setBatchNumPages] = useState(0);

  // UTMs
  const [utmSource, setUtmSource] = useState('whatsapp');
  const [utmMedium, setUtmMedium] = useState('email');
  const [utmCampaignName, setUtmCampaignName] = useState('');
  const [utmContent, setUtmContent] = useState('pdf');
  const [utmTerm, setUtmTerm] = useState('base-geral');
  const [utmDate, setUtmDate] = useState('');

  useEffect(() => {
    const agora = new Date();
    const d = String(agora.getDate()).padStart(2, '0');
    const m = String(agora.getMonth() + 1).padStart(2, '0');
    const y = agora.getFullYear();
    setUtmDate(`${d}-${m}-${y}`);
  }, []);

  const getCampaignFinal = useCallback(() => {
    const limpo = utmCampaignName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const reg = /^\d{2}-\d{2}-\d{4}$/;
    if (reg.test(utmDate)) {
      const [d, m, y] = utmDate.split('-');
      return `${y}-${m}-${d}-${limpo}`;
    }
    return limpo;
  }, [utmCampaignName, utmDate]);

  const buildUrlComUtm = useCallback((baseUrl) => {
    if (!baseUrl) return "";
    try {
      const url = new URL(baseUrl.startsWith('http') ? baseUrl : `https://${baseUrl}`);
      url.searchParams.set('utm_source', utmSource);
      url.searchParams.set('utm_medium', utmMedium);
      url.searchParams.set('utm_campaign', getCampaignFinal());
      url.searchParams.set('utm_content', utmContent);
      url.searchParams.set('utm_term', utmTerm);
      return url.toString();
    } catch (e) { return baseUrl; }
  }, [utmSource, utmMedium, utmContent, utmTerm, getCampaignFinal]);

  // --- MEMOIZAÇÃO DO PDF PARA PERFORMANCE ---
  const memoizedPDF = useMemo(() => {
    if (!file) return null;
    return (
      <Document file={file} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
        <Page
          pageNumber={pageNumber}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          scale={1.3}
        />
      </Document>
    );
  }, [file, pageNumber]);

  // --- HANDLERS MOUSE (CORRIGIDOS PARA CAMADA SOBREPOSTA) ---
  const onMouseDown = (e) => {
    if (!file || e.button !== 0 || editingId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setStartPos({ x, y });
    setIsDrawing(true);
    setCurrentRect({ left: x, top: y, width: 0, height: 0 });
  };

  const onMouseMove = (e) => {
    if (!isDrawing) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCurrentRect({
      left: Math.min(x, startPos.x),
      top: Math.min(y, startPos.y),
      width: Math.abs(x - startPos.x),
      height: Math.abs(y - startPos.y),
    });
  };

  const onMouseUp = (e) => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (currentRect && currentRect.width > 10) {
      const rect = e.currentTarget.getBoundingClientRect();
      const newId = Date.now();
      const newLink = {
        ...currentRect,
        id: newId,
        baseUrl: '',
        page: pageNumber,
        renderWidth: rect.width,
        renderHeight: rect.height
      };
      setLinks(prev => [...prev, newLink]);
      setEditingId(newId);
      setTempUrl('');
    } else {
      setCurrentRect(null);
    }
  };

  const handleApplyLink = () => {
    setLinks(prev => prev.map(l => l.id === editingId ? { ...l, baseUrl: tempUrl } : l));
    setEditingId(null);
    setCurrentRect(null);
  };

  const saveFinalPdf = async () => {
    if (!file || links.length === 0) return alert("Adicione links primeiro!");
    try {
      const bytes = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(bytes);
      const pages = pdfDoc.getPages();
      links.forEach(link => {
        if (!link.baseUrl) return;
        const page = pages[link.page - 1];
        const { width, height } = page.getSize();
        const sX = width / link.renderWidth;
        const sY = height / link.renderHeight;
        const pdfX = link.left * sX;
        const pdfW = link.width * sX;
        const pdfH = link.height * sY;
        const pdfY = height - (link.top * sY) - pdfH;

        const annot = pdfDoc.context.obj({
          Type: 'Annot', Subtype: 'Link', Rect: [pdfX, pdfY, pdfX + pdfW, pdfY + pdfH],
          Border: [0, 0, 0], A: { Type: 'Action', S: 'URI', URI: PDFString.of(buildUrlComUtm(link.baseUrl)) }
        });
        const ref = pdfDoc.context.register(annot);
        let annots = page.node.get(PDFName.of('Annots'));
        if (!annots) { annots = pdfDoc.context.obj([]); page.node.set(PDFName.of('Annots'), annots); }
        annots.push(ref);
      });
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${file.name.replace('.pdf', '')}_links.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (err) { alert("Erro ao gerar PDF."); }
  };

  // Funções Batch Simplificadas
  const processBatchPdfs = async () => {
    if (batchFiles.length === 0) return alert("Selecione os arquivos!");
    for (const f of batchFiles) {
      const bytes = await f.arrayBuffer();
      const pdfDoc = await PDFDocument.load(bytes);
      const pages = pdfDoc.getPages();
      pages.forEach((page, idx) => {
        const url = batchLinksPerPage[idx + 1];
        if (!url) return;
        const { width, height } = page.getSize();
        const annot = pdfDoc.context.obj({
          Type: 'Annot', Subtype: 'Link', Rect: [0, 0, width, height],
          Border: [0, 0, 0], A: { Type: 'Action', S: 'URI', URI: PDFString.of(buildUrlComUtm(url)) }
        });
        const ref = pdfDoc.context.register(annot);
        let annots = page.node.get(PDFName.of('Annots'));
        if (!annots) { annots = pdfDoc.context.obj([]); page.node.set(PDFName.of('Annots'), annots); }
        annots.push(ref);
      });
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `BATCH_${f.name}`;
      a.click();
      URL.revokeObjectURL(downloadUrl);
    }
  };

  const memoizedLinkRects = useMemo(() => {
    console.log("Renderizando camada de links..."); // Para você ver no console que agora é raro
    return links
      .filter((l) => l.page === pageNumber)
      .map((link) => (
        <div
          key={link.id}
          className={`link-rect ${editingId === link.id ? 'editing' : ''}`}
          style={{
            left: link.left,
            top: link.top,
            width: link.width,
            height: link.height,
            position: 'absolute',
            zIndex: 110
          }}
          onClick={(e) => {
            e.stopPropagation();
            setTempUrl(link.baseUrl || '');
            setEditingId(link.id);
            setCurrentRect(link);
          }}
        >
          <button
            className="btn-delete-link"
            onClick={(e) => {
              e.stopPropagation();
              setLinks((prev) => prev.filter((l) => l.id !== link.id));
              if (editingId === link.id) setEditingId(null);
            }}
          >
            ×
          </button>
        </div>
      ));
  }, [links, pageNumber, editingId]);

  if (!editorMode) {
    return (
      <div className="setup-screen">
        <div className="setup-card">
          <h2>Editor de PDF</h2>
          <div className="setup-buttons">
            <button onClick={() => setEditorMode('single')} className="mode-btn">
              <strong>☝️ Individual</strong>
              <p>Desenhar links manuais no PDF.</p>
            </button>
            <button onClick={() => setEditorMode('batch')} className="mode-btn">
              <strong>📚 Em Lote</strong>
              <p>Mesmos links em vários arquivos.</p>
            </button>
          </div>
          <button onClick={() => navigate('/lojas')} className="back-link">Voltar para Lojas</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-editor-container">
      {/* BARRA DE UTM */}
      <div className="utm-generator-bar">
        <div className="utm-group">
          <label className="utm-label">UTM Source</label>
          <select className="utm-select" value={utmSource} onChange={e => setUtmSource(e.target.value)}>
            <option value="whatsapp">whatsapp</option><option value="email">email</option>
            <option value="meta">meta</option><option value="google">google</option><option value="sms">sms</option>
          </select>
        </div>
        <div className="utm-group">
          <label className="utm-label">UTM Medium</label>
          <select className="utm-select" value={utmMedium} onChange={e => setUtmMedium(e.target.value)}>
            <option value="email">email</option><option value="messaging">messaging</option>
            <option value="paid_search">paid_search</option><option value="paid_social">paid_social</option><option value="display">display</option>
          </select>
        </div>
        <div className="utm-group" style={{ flex: 2 }}>
          <label className="utm-label">UTM Campaign (Nome)</label>
          <input className="utm-input" type="text" value={utmCampaignName} onChange={e => setUtmCampaignName(e.target.value)} placeholder="ex: campanha-verao" />
        </div>
        <div className="utm-group">
          <label className="utm-label">UTM Content</label>
          <select className="utm-select" value={utmContent} onChange={e => setUtmContent(e.target.value)}>
            <option value="pdf">pdf</option><option value="newsletter">newsletter</option><option value="produto">produto</option>
          </select>
        </div>
        <div className="utm-group">
          <label className="utm-label">UTM Term</label>
          <select className="utm-select" value={utmTerm} onChange={e => setUtmTerm(e.target.value)}>
            <option value="base-geral">base-geral</option><option value="pdf">segmentado</option>
          </select>
        </div>
        <div className="utm-group">
          <label className="utm-label">Data (DD-MM-AAAA)</label>
          <input className="utm-input" type="text" value={utmDate} onChange={e => setUtmDate(e.target.value)} placeholder="DD-MM-AAAA" />
        </div>
        <button onClick={() => setEditorMode(null)} className="reset-mode-btn">Mudar Modo</button>
      </div>

      {editorMode === 'batch' && (
        <div className="batch-layout-container">
          <div className="batch-sidebar">
            <h3>Configurar Lote</h3>
            <input type="file" multiple accept=".pdf" onChange={async e => {
              const files = Array.from(e.target.files);
              setBatchFiles(files);
              const first = await PDFDocument.load(await files[0].arrayBuffer());
              setBatchNumPages(first.getPageCount());
            }} />
            {batchNumPages > 0 && (
              <div style={{ marginTop: '20px' }}>
                {Array.from({ length: batchNumPages }).map((_, i) => (
                  <div key={i} style={{ marginBottom: '15px' }}>
                    <label className="utm-label">URL Página {i + 1}</label>
                    <input className="url-input-batch" type="text" placeholder="https://..." onChange={e => setBatchLinksPerPage({ ...batchLinksPerPage, [i + 1]: e.target.value })} />
                  </div>
                ))}
                <button onClick={processBatchPdfs} className="btn-process-batch">GERAR TODOS</button>
              </div>
            )}
          </div>
          <div className="batch-preview-empty">📚 Aplicando links em lote...</div>
        </div>
      )}

      {editorMode === 'single' && (
        <>
          <nav className="editor-toolbar">
            <div className="toolbar-group">
              <button onClick={() => navigate('/lojas')} className="btn-back">← Sair</button>
              <input type="file" onChange={e => setFile(e.target.files[0])} accept=".pdf" />
            </div>
            {file && (
              <div className="toolbar-group">
                <button onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}>⬅️</button>
                <span>{pageNumber} / {numPages}</span>
                <button onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}>➡️</button>
                <button onClick={saveFinalPdf} className="btn-save-main">💾 SALVAR PDF</button>
              </div>
            )}
          </nav>

          <div className="editor-scroll-area">
            <div className="pdf-render-wrapper" style={{ position: 'relative', display: 'block' }}>
              {file && (
                <>
                  {/* CAMADA DO PDF */}
                  <div className="pdf-container-view">
                    {memoizedPDF}
                  </div>

                  {/* CAMADA DE INTERAÇÃO (ESSENCIAL PARA O DESENHO) */}
                  <div
                    className="drawing-canvas-layer"
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 100,
                      cursor: 'crosshair'
                    }}
                  >
                    {/* AQUI ENTRAM OS LINKS MEMOIZADOS */}
                    {memoizedLinkRects}

                    {/* RETÂNGULO QUE VOCÊ ESTÁ DESENHANDO NO MOMENTO */}
                    {currentRect && !editingId && (
                      <div
                        className="link-rect-active"
                        style={{
                          left: currentRect.left,
                          top: currentRect.top,
                          width: currentRect.width,
                          height: currentRect.height,
                          position: 'absolute',
                          border: '2px dashed #3b82f6',
                          backgroundColor: 'rgba(59, 130, 246, 0.2)',
                          pointerEvents: 'none' // Evita que o retângulo em desenho bloqueie o mouseup
                        }}
                      />
                    )}
                  </div>

                  {/* EDITOR FLUTUANTE - AGORA FORA DA CAMADA DE DESENHO PARA NÃO GERAR CONFLITO */}
                  {editingId && (
                    <div
                      className="floating-url-editor"
                      style={{
                        position: 'absolute',
                        left: currentRect.left,
                        top: currentRect.top + currentRect.height + 10,
                        zIndex: 1000 // Fica acima de tudo
                      }}
                    >
                      <input
                        autoFocus
                        className="floating-input"
                        value={tempUrl}
                        onChange={e => setTempUrl(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleApplyLink();
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        placeholder="Cole a URL aqui"
                      />
                      <button className="btn-confirm-link" onClick={handleApplyLink}>APLICAR</button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PdfEditor;